import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useBilling } from '../hooks/useBilling';
import type { RevisionReason } from '../types/billing.types';

interface RevisionReasonModalProps {
  open: boolean;
  billNumber: string;
  onSubmit: (data: { revision_reason_id?: string; revision_reason_notes?: string }) => void;
  onCancel: () => void;
}

const RevisionReasonModal: React.FC<RevisionReasonModalProps> = ({
  open,
  billNumber,
  onSubmit,
  onCancel,
}) => {
  const { fetchRevisionReasons } = useBilling();
  const [reasons, setReasons] = useState<RevisionReason[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [selectedReasonId, setSelectedReasonId] = useState<string | null>(null);
  const [customNotes, setCustomNotes] = useState('');

  // Fetch reasons from API when modal opens
  useEffect(() => {
    if (open) {
      setLoadingReasons(true);
      fetchRevisionReasons()
        .then((data) => setReasons(data))
        .catch(() => {}) // error surfaced via hook
        .finally(() => setLoadingReasons(false));
    }
  }, [open, fetchRevisionReasons]);

  if (!open) return null;

  const selectedReason = reasons.find((r) => r.revision_reason_id === selectedReasonId);
  const previewText = selectedReason?.label || (!selectedReasonId && customNotes ? customNotes : '');

  const handleReasonClick = (reasonId: string) => {
    if (selectedReasonId === reasonId) {
      setSelectedReasonId(null);
    } else {
      setSelectedReasonId(reasonId);
    }
  };

  const handleNotesChange = (val: string) => {
    setCustomNotes(val);
    // If typing custom notes without a selected reason, that's fine — both can coexist
  };

  const handleSubmit = () => {
    onSubmit({
      revision_reason_id: selectedReasonId || undefined,
      revision_reason_notes: customNotes.trim() || undefined,
    });
    setSelectedReasonId(null);
    setCustomNotes('');
  };

  const handleCancel = () => {
    setSelectedReasonId(null);
    setCustomNotes('');
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={handleCancel}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal */}
      <div
        className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-orange-100 dark:bg-orange-900/30">
              <ArrowPathIcon className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                Request Revision
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {billNumber}
              </p>
            </div>
          </div>
          <button
            onClick={handleCancel}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This will return the invoice to draft status for edits. Select a reason or provide your own.
          </p>

          {/* Preset reasons from API */}
          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              Common Reasons
            </label>
            {loadingReasons ? (
              <div className="flex items-center gap-2 py-4">
                <div className="h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm text-gray-400">Loading reasons...</span>
              </div>
            ) : reasons.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {reasons.map((reason) => (
                  <button
                    key={reason.revision_reason_id}
                    type="button"
                    onClick={() => handleReasonClick(reason.revision_reason_id)}
                    title={reason.description || reason.label}
                    className={`text-left px-3 py-2 text-sm rounded-lg border transition-colors ${
                      selectedReasonId === reason.revision_reason_id
                        ? 'border-orange-400 bg-orange-50 text-orange-800 dark:border-orange-500 dark:bg-orange-900/20 dark:text-orange-300'
                        : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    {reason.label}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">No preset reasons configured.</p>
            )}
          </div>

          {/* Custom notes */}
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
              Additional notes (optional)
            </label>
            <textarea
              rows={3}
              value={customNotes}
              onChange={(e) => handleNotesChange(e.target.value)}
              placeholder="Describe what needs to be changed..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>

          {/* Preview */}
          {(previewText || customNotes.trim()) && (
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2">
              <div className="text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-0.5">
                Reason to be recorded
              </div>
              {selectedReason && (
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{selectedReason.label}</p>
              )}
              {selectedReason?.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{selectedReason.description}</p>
              )}
              {customNotes.trim() && (
                <p className={`text-sm text-gray-600 dark:text-gray-400 ${selectedReason ? 'mt-1 italic' : ''}`}>
                  {customNotes.trim()}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            Request Revision
          </button>
        </div>
      </div>
    </div>
  );
};

export default RevisionReasonModal;
